import { motion, useReducedMotion } from "motion/react";
import { MARK_PARTS, WORDMARK, ARABIC_WORDMARK } from "@mashriqain/brand";

interface MarkProps {
  size?: number | string;
  /** Draw the mark stroke by stroke when it enters the viewport. */
  draw?: boolean;
  className?: string;
  title?: string;
}

export function Mark({ size = 40, draw = false, className, title }: MarkProps) {
  const reduce = useReducedMotion();
  const animate = draw && !reduce;
  return (
    <svg
      viewBox="0 0 200 200"
      width={size}
      height={size}
      className={className}
      role={title ? "img" : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      {MARK_PARTS.map((part, i) =>
        part.fill ? (
          <motion.path
            key={part.name}
            d={part.d}
            fill="currentColor"
            initial={animate ? { opacity: 0, y: 14 } : false}
            whileInView={animate ? { opacity: 1, y: 0 } : undefined}
            viewport={{ once: true }}
            transition={{ duration: 1.1, delay: 1.6, ease: [0.22, 1, 0.36, 1] }}
          />
        ) : (
          <motion.path
            key={part.name}
            d={part.d}
            fill="none"
            stroke="currentColor"
            strokeWidth={part.stroke}
            initial={animate ? { pathLength: 0, opacity: 0 } : false}
            whileInView={animate ? { pathLength: 1, opacity: 1 } : undefined}
            viewport={{ once: true }}
            transition={{ duration: 1.6, delay: i * 0.18, ease: [0.65, 0, 0.35, 1] }}
          />
        ),
      )}
    </svg>
  );
}

/** Horizontal lockup used in the header: mark plus the Latin wordmark. */
export function Logo({ height = 34, withArabic = false }: { height?: number; withArabic?: boolean }) {
  const gap = 34;
  const textX = 200 + gap;
  const scale = withArabic ? 1 : 1.12;
  const width = Math.ceil(textX + WORDMARK.width * scale + 4);
  return (
    <svg viewBox={`0 0 ${width} 200`} height={height} width={(height * width) / 200} aria-hidden="true">
      {MARK_PARTS.map((part) =>
        part.fill ? (
          <path key={part.name} d={part.d} fill="currentColor" />
        ) : (
          <path key={part.name} d={part.d} fill="none" stroke="currentColor" strokeWidth={(part.stroke ?? 0) * 1.25} />
        ),
      )}
      <path d={WORDMARK.d} transform={`translate(${textX} ${withArabic ? 112 : 130}) scale(${scale})`} fill="currentColor" />
      {withArabic && (
        <path
          d={ARABIC_WORDMARK.d}
          transform={`translate(${textX + WORDMARK.width - ARABIC_WORDMARK.width} 182)`}
          fill="currentColor"
        />
      )}
    </svg>
  );
}
